<template>
  <article class="image-selector-wrapper">
    <div
      v-if="carouselImages.length > IMAGE_PER_VIEW && isVertical && isMounted"
      class="chevron-up-wrap"
      :class="{ invisible: altImagePage === 1 }"
      role="button"
      tabindex="0"
      aria-label="Previous"
      @click="prev"
      @keydown.enter="prev"
      @keydown.space.prevent="prev"
    >
      <div class="chevron-large-up-dark-medium chevron-large" aria-label="scroll up images rail"></div>
    </div>
    <div v-if="isMounted && isVertical" class="alt-image-container">
      <ul class="alt-desktop-images" :style="{ transform: 'translate(' + '0px, ' + initValue + 'px)' }" role="tablist" aria-label="carousel">
        <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -->
        <li
          v-for="(src, i) in props.carouselImages"
          :key="i"
          :ref="(el) => el && (listItemRefs[i] = el)"
          class="image-wrapper"
          :class="[{ active: currentPage - 1 === i }, { hovered: currentPageOnHover === i }, { 'video-wrapper': src.video }]"
          role="tab"
          :tabindex="currentPage - 1 === i ? 0 : -1"
          :aria-selected="currentPage - 1 === i"
          v-bind="ariaData[i]"
          @click="selectImage(i)"
          @mouseover="enterMouse(i)"
          @mouseleave="leaveMouse"
          @keydown.enter="selectImage(i)"
          @keydown.space.prevent="selectImage(i)"
          @focus="enterMouse(i)"
          @blur="leaveMouse"
          @keydown.left.prevent="handleKeyDown($event, i)"
          @keydown.right.prevent="handleKeyDown($event, i)"
        >
          <AltImageItem
            :index="i"
            :data="src"
            :video-id="videoId"
            :images="props.carouselImages"
            :current-page="currentPage"
            :seo-alt="props.seoAlt"
          />
        </li>
      </ul>
    </div>
    <div v-if="!isVertical" class="alt-image-container alt-image-container--vertical">
      <div
        class="carousel-image-for-tablet margin-top-xs"
        :class="{
          last: isLastPage,
          full: carouselImages.length <= IMAGE_PER_VIEW_FOR_TABLET,
        }"
      >
        <Carousel
          v-if="isMounted"
          ref="altCarousel"
          :list="carouselImages"
          :number-of-items="{ large: 4, medium: 4, small: 4 }"
          :gutter="{ large: 8, medium: 8, small: 8 }"
          @carousel-next="nextClick"
          @carousel-prev="prevClick"
        >
          <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
          <template #item="{ data, index }">
            <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
            <!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
            <!-- eslint-disable vuejs-accessibility/click-events-have-key-event -->
            <div
              :key="index"
              :ref="
                (el) => {
                  if (el) listItemRefs[index] = el;
                }
              "
              :tabindex="currentPage - 1 === index ? 0 : -1"
              :aria-selected="currentPage - 1 === index"
              v-bind="ariaData[index]"
              aria-label="Select image or video thumbnail"
              class="image-wrapper horizontal"
              :class="[{ active: currentPage - 1 === index }, { hovered: currentPageOnHover === index }, { 'video-wrapper': data.video }]"
              @click="selectImage(index)"
              @mouseover="enterMouse(index)"
              @mouseleave="leaveMouse"
              @keydown.left.prevent="handleKeyDown($event, index)"
              @keydown.right.prevent="handleKeyDown($event, index)"
            >
              <AltImageItem
                :index="index"
                :data="data"
                :video-id="videoId"
                :images="props.carouselImages"
                :current-page="currentPage"
                :seo-alt="props.seoAlt"
              />
            </div>
          </template>
        </Carousel>
      </div>
    </div>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
    <div
      v-if="carouselImages.length > IMAGE_PER_VIEW && isVertical && isMounted"
      class="chevron-down-wrap"
      :class="{
        invisible: hideNextArrow || props.carouselImages.length === pageRanges.last,
      }"
      @click="next"
    >
      <div class="chevron-large-down-dark-medium chevron-large" aria-label="scroll down images rail"></div>
    </div>
  </article>
</template>
<script setup>
import { useMounted } from '@vueuse/core';
import { ref, watch, computed, reactive, inject } from 'vue';
import Carousel from '@atomic-ui/carousel/Carousel';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useBrand } from '~/composables/useBrand';
import AltImageItem from './AltImageItem';
import { ALT_IMAGE_ORIENTATION } from '../../../constants/common';
import useImages from '~/composables/images/useImages';
import { tagImageClicked } from '~/features/analytics/image';

const triggerAltImageClickOnce = inject('triggerAltImageClickOnce', () => {});

const ALT_IMAGE_SIZE_FOR_DESKTOP_LARGE = 110;
const ALT_IMAGE_SIZE_FOR_DESKTOP_MEDIUM = 63;
const IMAGE_PER_VIEW = 5;
const IMAGE_PER_VIEW_FOR_TABLET = 4;

const { videoId } = useImages();
const isMounted = useMounted();
const { isBcom, isMcom } = useBrand();

const emits = defineEmits(['imageChange', 'imageChangeOnHover']);
const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  currentPageOnHover: {
    /** @type {import('vue').PropType<number | null>} */
    type: Number || null,
    default: null,
  },
  carouselImages: {
    /** @type {import('vue').PropType<Image[]>} */
    type: Array,
    required: true,
  },
  orientation: {
    type: String,
    default: ALT_IMAGE_ORIENTATION.vertical,
    validator(orientation) {
      return Object.values(ALT_IMAGE_ORIENTATION).includes(orientation);
    },
  },
  seoAlt: {
    type: String,
    default: '',
  },
});

const { isTablet, isDesktopMedium } = useDeviceType();

const listItemRefs = reactive([]);

const initValue = ref(0);
const timeOut = ref(0);
const isLastPage = ref(false);
const hideNextArrow = ref(false);
const hidePrevArrow = ref(true);
const altImagePage = ref(1);
const pageRanges = ref({
  first: 1,
  last: IMAGE_PER_VIEW,
});
const tabletPageRanges = ref({
  first: 1,
  last: IMAGE_PER_VIEW_FOR_TABLET,
});
const altCarousel = ref(null);
const ariaData = computed(() =>
  props.carouselImages.map((mediaItem, i) => ({
    'aria-label': `Display ${props.seoAlt} ${mediaItem.video ? 'Video' : 'Image'}`,
    'aria-posinset': i + 1,
    'aria-setsize': props.carouselImages.length,
  })),
);
const isVertical = computed(() => props.orientation === ALT_IMAGE_ORIENTATION.vertical);
/**
 * @param {number} index
 */
const selectImage = (index) => {
  emits('imageChange', index + 1);
  if (isBcom) return tagImageClicked(index + 1);
  if (isMcom) return triggerAltImageClickOnce();
};
const canMoveByUnit = computed(() => (props.carouselImages.length - altImagePage.value * IMAGE_PER_VIEW) / IMAGE_PER_VIEW);
const altImageSize = computed(() => {
  if ((props.orientation === ALT_IMAGE_ORIENTATION.vertical && isTablet.value) || isDesktopMedium.value) {
    return ALT_IMAGE_SIZE_FOR_DESKTOP_MEDIUM;
  }
  return ALT_IMAGE_SIZE_FOR_DESKTOP_LARGE;
});
const resetFields = () => {
  initValue.value = 0;
  altImagePage.value = 1;
  hideNextArrow.value = false;
  pageRanges.value = {
    first: 1,
    last: IMAGE_PER_VIEW,
  };
  tabletPageRanges.value = {
    first: 1,
    last: IMAGE_PER_VIEW_FOR_TABLET,
  };
};

const prev = () => {
  if (altImagePage.value === 1) return;
  hideNextArrow.value = false;
  if (altImagePage.value === 2) {
    resetFields();
    return;
  }

  initValue.value += altImageSize.value * IMAGE_PER_VIEW;

  if (altImagePage.value > 1) {
    altImagePage.value -= 1;
  }
  if (props.carouselImages.length % IMAGE_PER_VIEW > 0 && altImagePage.value + 1 === Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW)) {
    pageRanges.value = {
      first: pageRanges.value.first - IMAGE_PER_VIEW,
      last: pageRanges.value.last - (props.carouselImages.length % IMAGE_PER_VIEW),
    };
    return;
  }

  pageRanges.value = {
    first: pageRanges.value.first - IMAGE_PER_VIEW,
    last: pageRanges.value.last - IMAGE_PER_VIEW,
  };
};
const next = () => {
  if (hideNextArrow.value || props.carouselImages.length === pageRanges.value.last) return;
  if (props.carouselImages.length > IMAGE_PER_VIEW && canMoveByUnit.value >= 0) {
    initValue.value -= altImageSize.value * IMAGE_PER_VIEW;
  } else if (props.carouselImages.length > IMAGE_PER_VIEW && canMoveByUnit.value < 0) {
    initValue.value -= altImageSize.value * ((props.carouselImages.length - (altImagePage.value - 1) * IMAGE_PER_VIEW) % IMAGE_PER_VIEW);
    hideNextArrow.value = true;
  }
  pageRanges.value = {
    first: pageRanges.value.last + 1,
    last: pageRanges.value.last + IMAGE_PER_VIEW > props.carouselImages.length ? props.carouselImages.length : pageRanges.value.last + IMAGE_PER_VIEW,
  };
  if (props.carouselImages.length % IMAGE_PER_VIEW > 0 && altImagePage.value === Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW)) {
    pageRanges.value = {
      first: props.carouselImages.length - 4,
      last: props.carouselImages.length,
    };
  }
  altImagePage.value += 1;
  hidePrevArrow.value = false;
};
/**
 * @param {number} index
 */
const enterMouse = (index) => {
  clearTimeout(timeOut.value);
  emits('imageChangeOnHover', index);
};
const leaveMouse = () => {
  timeOut.value = setTimeout(() => {
    emits('imageChangeOnHover', null);
  }, 10);
};
const onCarouselPageChange = (newPage) => {
  if (!newPage || newPage < 1) return;

  const totalPages = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW_FOR_TABLET);
  isLastPage.value = newPage === totalPages;

  const firstThumbnailOfPage = (newPage - 1) * IMAGE_PER_VIEW_FOR_TABLET + 1;
  const lastThumbnailOfPage = isLastPage.value ? props.carouselImages.length : newPage * IMAGE_PER_VIEW_FOR_TABLET;

  tabletPageRanges.value = { first: firstThumbnailOfPage, last: lastThumbnailOfPage };
};

const nextClick = (newPage) => {
  onCarouselPageChange(newPage);
};

const prevClick = (newPage) => {
  onCarouselPageChange(newPage);
};

/**
 * @param {KeyboardEvent} event
 * @param {number} index
 * Handles Arrow Key navigation and focus management.
 */
const handleKeyDown = (event, index) => {
  const numImages = props.carouselImages.length;
  let nextIndex;

  if (event.key === 'ArrowRight') {
    nextIndex = (index + 1) % numImages;
  } else if (event.key === 'ArrowLeft') {
    nextIndex = (index - 1 + numImages) % numImages;
  }

  emits('imageChange', nextIndex + 1);

  const nextTabElement = listItemRefs[nextIndex];
  nextTabElement.focus();
};

const controlTabletNavigation = (value) => {
  if (props.carouselImages.length <= IMAGE_PER_VIEW_FOR_TABLET || !altCarousel.value) return;

  const targetPage = Math.ceil(value / IMAGE_PER_VIEW_FOR_TABLET);
  const currentPage = Math.ceil(tabletPageRanges.value.first / IMAGE_PER_VIEW_FOR_TABLET);
  const pageDifference = targetPage - currentPage;

  if (pageDifference > 0) {
    Array.from({ length: pageDifference }).forEach(() => {
      altCarousel.value.next();
    });
  } else if (pageDifference < 0) {
    Array.from({ length: -pageDifference }).forEach(() => {
      altCarousel.value.prev();
    });
  }
};

const controlDesktopNavigation = (value) => {
  if (props.carouselImages.length <= IMAGE_PER_VIEW) return;
  if (value === 1) {
    // move alt images to start point
    resetFields();
  } else if (value === props.carouselImages.length && Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW) > 1) {
    // move alt images to last point
    if (pageRanges.value.last === props.carouselImages.length) return;
    if (canMoveByUnit.value < 0) {
      initValue.value = -(
        altImageSize.value *
        (IMAGE_PER_VIEW * (altImagePage.value - 1) + (props.carouselImages.length % IMAGE_PER_VIEW) - IMAGE_PER_VIEW)
      );
      pageRanges.value = {
        first: pageRanges.value.last + 1,
        last:
          pageRanges.value.last + IMAGE_PER_VIEW > props.carouselImages.length ? props.carouselImages.length : pageRanges.value.last + IMAGE_PER_VIEW,
      };
    } else {
      const pages = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW);
      pageRanges.value = {
        first: (pages - 1) * IMAGE_PER_VIEW + 1,
        last: props.carouselImages.length,
      };
      initValue.value = -((pages - 1) * IMAGE_PER_VIEW) * altImageSize.value;
    }
    altImagePage.value = Math.ceil(props.carouselImages.length / IMAGE_PER_VIEW);
    hideNextArrow.value = true;
  } else if (value > pageRanges.value.last) {
    next();
  } else if (value < pageRanges.value.first) {
    prev();
  }
};

watch(
  () => props.currentPage,
  (value) => {
    if (isTablet.value && altCarousel.value && props.orientation === ALT_IMAGE_ORIENTATION.horizontal) {
      controlTabletNavigation(value);
    } else {
      controlDesktopNavigation(value);
    }
  },
);

watch(
  () => props.carouselImages.length,
  () => {
    resetFields();
  },
);
</script>
<style lang="scss" scoped>
@import '~@csgIcons/new/chevron-large-down-dark-medium';
@import '~@csgIcons/new/chevron-large-up-dark-medium';
@import '~settingSCSS';
.alt-desktop-images {
  list-style-type: none;
}
.alt-desktop-image {
  margin-bottom: rem-calc(8);
}
.alt-image-container {
  max-height: rem-calc(542);
  overflow: hidden;
  ul {
    transition: 0.2s ease;
    overflow: hidden;
  }
  @include breakpoint(large down) {
    max-height: rem-calc(307);
  }
}
.alt-image-container--vertical {
  height: rem-calc(75+16); // 75px image height + 16px margin
}
.image-wrapper {
  position: relative;
  margin-bottom: rem-calc(8);
  transition: 0.2s ease;
  opacity: 0.8;
  display: flex;
  align-items: center;
  width: fit-content;
  &.video-wrapper {
    opacity: 1;
    :deep() {
      .picture-container {
        opacity: 0.5;
        transition: 0.2s ease;
      }
    }
  }
  &.hovered,
  &.active {
    opacity: 1;
    :deep() {
      .video {
        .picture-container {
          opacity: 1;
        }
      }
    }
  }
  &.horizontal {
    margin-bottom: 0;
    :deep {
      .alt-img {
        @include breakpoint(medium down) {
          width: rem-calc(60);
          height: rem-calc(75);
          max-height: rem-calc(75);
        }
      }
    }
  }
}
.image-selector-wrapper {
  height: 100%;
}
.chevron-down-wrap {
  margin-top: rem-calc(8);
}
.chevron-up-wrap {
  margin-bottom: rem-calc(8);
}
.chevron-up-wrap,
.chevron-down-wrap {
  width: rem-calc(82);
  display: flex;
  justify-content: center;
  cursor: pointer;
  &.disabled {
    opacity: 0.35;
    cursor: default;
  }
  .chevron-large {
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    @include breakpoint(large down) {
      margin: unset;
    }
  }
  @include breakpoint(large down) {
    width: rem-calc(44);
  }

  @include breakpoint(large) {
    display: flex;
  }
}
.carousel-image-for-tablet {
  display: none;
  @include breakpoint(medium only) {
    display: block;
  }
  :deep() {
    .v-carousel {
      margin-left: 46px !important;
      margin-right: 46px !important;

      .v-carousel-content {
        overflow: visible;
      }
    }
    .v-carousel-wrapper {
      position: unset;
    }
    .v-button-wrapper.prev {
      left: -40px;
    }
    .v-button-wrapper.next {
      right: -40px;
    }
    .v-carousel-button {
      display: flex;
      background-color: transparent;
      &[tabindex='-1'] {
        display: none;
      }
    }
    .v-button-wrapper {
      display: flex !important;
      transform: translateX(0);
    }
  }
  &.last {
    :deep() {
      .v-carousel {
        margin-right: 92px !important;
      }
      .v-carousel-wrapper {
        margin-right: -46px !important;
      }
      .v-button-wrapper.next {
        right: -90px;
      }
      .v-scroller {
        margin-left: -20px !important;
      }
    }
  }
  &.full {
    :deep() {
      .v-carousel {
        margin-left: 0px !important;
        margin-right: 92px !important;
      }
      .v-button-wrapper {
        display: none !important;
      }
    }
  }
}
</style>
